import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Boxes from "../../components/main/boxes";
import NuvemTreinamento from "../../components/NuvemTreinamento";
import Lanyard from "../../components/Lanyard";
import { db } from "../../services/firebaseConnection";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import "./main.css";

const CORES_GRAFICO = [
  "#2563eb",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#14b8a6",
  "#f97316",
];

const MESES_PT = [
  "JANEIRO",
  "FEVEREIRO",
  "MARÇO",
  "ABRIL",
  "MAIO",
  "JUNHO",
  "JULHO",
  "AGOSTO",
  "SETEMBRO",
  "OUTUBRO",
  "NOVEMBRO",
  "DEZEMBRO",
];

const normalizarTexto = (texto) => {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
};

const converterParaData = (valor) => {
  if (!valor) return null;
  if (valor?.toDate) return valor.toDate();

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(valor))) {
    const [ano, mes, dia] = String(valor).split("-");
    return new Date(Number(ano), Number(mes) - 1, Number(dia));
  }

  const dataConvertida = new Date(valor);
  return isNaN(dataConvertida.getTime()) ? null : dataConvertida;
};

const treinamentoFoiSubstituido = (treinamento) => {
  return (
    treinamento.substituido === true ||
    normalizarTexto(treinamento.statusLancamento) === "SUBSTITUIDO" ||
    normalizarTexto(treinamento.situacaoLancamento) === "SUBSTITUIDO"
  );
};

const pegarDataLancamento = (treinamento) => {
  return (
    converterParaData(treinamento.dataCadastro) ||
    converterParaData(treinamento.dataLancamento) ||
    converterParaData(treinamento.cadastradoEm) ||
    converterParaData(treinamento.criadoEm) ||
    converterParaData(treinamento.createdAt) ||
    converterParaData(treinamento.data)
  );
};

const pegarObraFuncionario = (funcionario, treinamento) => {
  return (
    treinamento.obraNaData ||
    treinamento.obra ||
    treinamento.obraAtual ||
    treinamento.obra_atual ||
    treinamento.obraAtualNome ||
    funcionario.obra ||
    funcionario.obraAtual ||
    funcionario.obra_atual ||
    funcionario.obraAtualNome ||
    "NÃO INFORMADA"
  );
};

const pegarResponsavelLancamento = (treinamento) => {
  return normalizarTexto(
    treinamento.cadastradoPor ||
      treinamento.cadastradopor ||
      treinamento.atualizadoPor ||
      treinamento.avaliador ||
      "NÃO INFORMADO",
  );
};

const getTrainingStatus = (
  trainingDate,
  validityMonths,
  possuiVencimento = true,
) => {
  if (possuiVencimento === false) return "none";
  if (!trainingDate || !validityMonths) return "none";

  const trainingDateObj = converterParaData(trainingDate);
  if (!trainingDateObj) return "none";

  const expirationDate = new Date(trainingDateObj);
  expirationDate.setMonth(
    expirationDate.getMonth() + parseInt(validityMonths, 10),
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expirationDate.setHours(0, 0, 0, 0);

  const daysDiff = Math.ceil(
    (expirationDate.getTime() - today.getTime()) / (1000 * 3600 * 24),
  );

  if (daysDiff < 0) return "expired";
  if (daysDiff <= 15) return "danger";
  if (daysDiff <= 30) return "warning";

  return "none";
};

const criarChaveMes = (data) => {
  if (!data) return "";
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
};

const criarLabelMes = (ano, mes) => `${MESES_PT[mes]} DE ${ano}`;

const deslocarMes = (dataBase, quantidade) => {
  return new Date(dataBase.getFullYear(), dataBase.getMonth() + quantidade, 1);
};

const obterMesesComparativo = () => {
  const hoje = new Date();

  return Array.from({ length: 12 }).map((_, index) => {
    const data = deslocarMes(
      new Date(hoje.getFullYear(), hoje.getMonth(), 1),
      -index,
    );

    return {
      chave: criarChaveMes(data),
      label: criarLabelMes(data.getFullYear(), data.getMonth()),
      ano: data.getFullYear(),
      mes: data.getMonth(),
    };
  });
};

const Main = () => {
  const navigate = useNavigate();
  const { usuarioLogado } = useAuth();

  const [alertStatus, setAlertStatus] = useState("none");
  const [loading, setLoading] = useState(true);
  const [mostrarLancamentos, setMostrarLancamentos] = useState(false);
  const [mostrarComparativo, setMostrarComparativo] = useState(false);

  const [mesSelecionado, setMesSelecionado] = useState(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  });

  const [mesesComparativo, setMesesComparativo] = useState(() => {
    const opcoes = obterMesesComparativo();
    return opcoes.slice(0, 2).map((item) => item.chave);
  });

  const [dadosDashboard, setDadosDashboard] = useState({
    totalMes: 0,
    porUsuario: [],
    porObra: [],
    todosLancamentos: [],
    estatisticasPorMes: {},
  });

  const primeiroNome = usuarioLogado?.nome?.split(" ")[0] || "";
  const sexoUsuario = usuarioLogado?.sexo?.toUpperCase() || "";
  const saudacao = sexoUsuario === "FEMININO" ? "Bem-vinda" : "Bem-vindo";

  const chaveMesSelecionado = useMemo(
    () => criarChaveMes(mesSelecionado),
    [mesSelecionado],
  );

  const mesAtualTexto = useMemo(() => {
    return criarLabelMes(
      mesSelecionado.getFullYear(),
      mesSelecionado.getMonth(),
    );
  }, [mesSelecionado]);

  const opcoesComparativo = useMemo(() => obterMesesComparativo(), []);

  const buscarDadosMain = useCallback(async () => {
    try {
      setLoading(true);

      let statusGeral = "none";
      const todosLancamentos = [];

      const funcionariosQuery = query(
        collection(db, "funcionarios"),
        orderBy("nome"),
      );
      const funcionariosSnapshot = await getDocs(funcionariosQuery);

      const funcionariosAtivos = funcionariosSnapshot.docs.filter(
        (funcionarioDoc) => {
          const funcionario = funcionarioDoc.data();
          return (
            normalizarTexto(funcionario.situacao || "ATIVO") !== "DESATIVADO"
          );
        },
      );

      const promessas = funcionariosAtivos.map(async (funcionarioDoc) => {
        const funcionario = funcionarioDoc.data();
        const funcionarioId = funcionarioDoc.id;

        let statusFuncionario = "none";

        const treinamentosRef = collection(
          db,
          "funcionarios",
          funcionarioId,
          "treinamentos",
        );
        const treinamentosSnapshot = await getDocs(treinamentosRef);

        treinamentosSnapshot.forEach((treinamentoDoc) => {
          const treinamento = treinamentoDoc.data();

          if (treinamentoFoiSubstituido(treinamento)) return;

          const possuiVencimento =
            treinamento.possuiVencimento !== undefined
              ? Boolean(treinamento.possuiVencimento)
              : true;

          const statusTreinamento = getTrainingStatus(
            treinamento.data,
            treinamento.validadeMeses,
            possuiVencimento,
          );

          if (statusTreinamento === "expired") {
            statusFuncionario = "expired";
          } else if (
            statusTreinamento === "danger" &&
            statusFuncionario !== "expired"
          ) {
            statusFuncionario = "danger";
          } else if (
            statusTreinamento === "warning" &&
            statusFuncionario === "none"
          ) {
            statusFuncionario = "warning";
          }

          const dataLancamento = pegarDataLancamento(treinamento);
          if (!dataLancamento) return;

          const obra = normalizarTexto(
            pegarObraFuncionario(funcionario, treinamento),
          );
          const responsavel = pegarResponsavelLancamento(treinamento);
          const chaveMes = criarChaveMes(dataLancamento);

          todosLancamentos.push({
            id: treinamentoDoc.id,
            funcionarioId,
            funcionarioNome: normalizarTexto(
              treinamento.colaboradorNome ||
                funcionario.nome ||
                "NÃO INFORMADO",
            ),
            treinamentoNome: normalizarTexto(
              treinamento.treinamentoNome ||
                treinamento.nome ||
                treinamento.tipo ||
                "NÃO INFORMADO",
            ),
            obra,
            responsavel,
            chaveMes,
            dataLancamento,
          });
        });

        return statusFuncionario;
      });

      const resultadosStatus = await Promise.all(promessas);

      if (resultadosStatus.includes("expired")) {
        statusGeral = "expired";
      } else if (resultadosStatus.includes("danger")) {
        statusGeral = "danger";
      } else if (resultadosStatus.includes("warning")) {
        statusGeral = "warning";
      }

      const estatisticasPorMes = {};

      todosLancamentos.forEach((lancamento) => {
        if (!estatisticasPorMes[lancamento.chaveMes]) {
          estatisticasPorMes[lancamento.chaveMes] = {
            total: 0,
            obras: {},
            usuarios: {},
          };
        }

        estatisticasPorMes[lancamento.chaveMes].total += 1;
        estatisticasPorMes[lancamento.chaveMes].obras[lancamento.obra] =
          (estatisticasPorMes[lancamento.chaveMes].obras[lancamento.obra] ||
            0) + 1;
        estatisticasPorMes[lancamento.chaveMes].usuarios[
          lancamento.responsavel
        ] =
          (estatisticasPorMes[lancamento.chaveMes].usuarios[
            lancamento.responsavel
          ] || 0) + 1;
      });

      setAlertStatus(statusGeral);
      setDadosDashboard((prev) => ({
        ...prev,
        todosLancamentos,
        estatisticasPorMes,
      }));
    } catch (error) {
      console.error("Erro ao carregar dados da Main:", error);

      setAlertStatus("none");
      setDadosDashboard({
        totalMes: 0,
        porObra: [],
        porUsuario: [],
        todosLancamentos: [],
        estatisticasPorMes: {},
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    buscarDadosMain();
  }, [buscarDadosMain]);

  useEffect(() => {
    const lancamentosDoMes = dadosDashboard.todosLancamentos.filter(
      (lancamento) => lancamento.chaveMes === chaveMesSelecionado,
    );

    const mapaObras = {};
    const mapaUsuarios = {};

    lancamentosDoMes.forEach((lancamento) => {
      if (!mapaObras[lancamento.obra]) {
        mapaObras[lancamento.obra] = { obra: lancamento.obra, quantidade: 0 };
      }

      mapaObras[lancamento.obra].quantidade += 1;

      if (!mapaUsuarios[lancamento.responsavel]) {
        mapaUsuarios[lancamento.responsavel] = {
          nome: lancamento.responsavel,
          quantidade: 0,
          obras: new Set(),
        };
      }

      mapaUsuarios[lancamento.responsavel].quantidade += 1;
      mapaUsuarios[lancamento.responsavel].obras.add(lancamento.obra);
    });

    const porObra = Object.values(mapaObras)
      .sort((a, b) => b.quantidade - a.quantidade)
      .map((item, index) => ({
        obra: item.obra,
        quantidade: item.quantidade,
        cor: CORES_GRAFICO[index % CORES_GRAFICO.length],
      }));

    const porUsuario = Object.values(mapaUsuarios)
      .map((item) => ({
        nome: item.nome,
        quantidade: item.quantidade,
        obras: Array.from(item.obras).join(", "),
      }))
      .sort((a, b) => b.quantidade - a.quantidade);

    setDadosDashboard((prev) => ({
      ...prev,
      totalMes: lancamentosDoMes.length,
      porObra,
      porUsuario,
    }));
  }, [chaveMesSelecionado, dadosDashboard.todosLancamentos]);

  const totalPorObra = dadosDashboard.porObra.reduce(
    (acc, item) => acc + item.quantidade,
    0,
  );

  const gradientePizza = useMemo(() => {
    if (!dadosDashboard.porObra.length || totalPorObra === 0) return "#e2e8f0";

    let inicio = 0;

    const partes = dadosDashboard.porObra.map((item) => {
      const porcentagem = (item.quantidade / totalPorObra) * 100;
      const fim = inicio + porcentagem;
      const parte = `${item.cor} ${inicio}% ${fim}%`;
      inicio = fim;
      return parte;
    });

    return `conic-gradient(${partes.join(", ")})`;
  }, [dadosDashboard.porObra, totalPorObra]);

  const alterarMes = (quantidade) => {
    setMesSelecionado((prev) => deslocarMes(prev, quantidade));
  };

  const voltarMesAtual = () => {
    const hoje = new Date();
    setMesSelecionado(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
  };

  const alternarMesComparativo = (chave) => {
    setMesesComparativo((prev) => {
      if (prev.includes(chave)) return prev.filter((item) => item !== chave);
      return [...prev, chave].sort();
    });
  };

  const dadosComparativo = useMemo(() => {
    return mesesComparativo.map((chave) => {
      const item = opcoesComparativo.find((mes) => mes.chave === chave);
      const estatistica = dadosDashboard.estatisticasPorMes[chave];

      return {
        chave,
        label: item?.label || chave,
        total: estatistica?.total || 0,
        obras: estatistica?.obras || {},
        usuarios: estatistica?.usuarios || {},
      };
    });
  }, [mesesComparativo, opcoesComparativo, dadosDashboard.estatisticasPorMes]);

  const maiorComparativo = useMemo(() => {
    const maior = Math.max(...dadosComparativo.map((item) => item.total), 0);
    return maior || 1;
  }, [dadosComparativo]);

  const baixarPdfComparativo = async () => {
    if (dadosComparativo.length < 2) {
      alert("SELECIONE PELO MENOS DOIS MESES PARA GERAR O COMPARATIVO.");
      return;
    }

    try {
      const jsPdfModule = await import("jspdf");
      const autoTableModule = await import("jspdf-autotable");

      const jsPDF = jsPdfModule.default;
      const autoTable = autoTableModule.default;

      const doc = new jsPDF("p", "mm", "a4");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("COMPARATIVO DE LANÇAMENTOS DE TREINAMENTOS", 14, 18);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`GERADO EM: ${new Date().toLocaleString("pt-BR")}`, 14, 25);

      autoTable(doc, {
        startY: 34,
        head: [["MÊS", "TOTAL DE LANÇAMENTOS"]],
        body: dadosComparativo.map((item) => [item.label, item.total]),
        styles: { font: "helvetica", fontSize: 9 },
        headStyles: { fillColor: [30, 58, 138], textColor: [255, 255, 255] },
      });

      let y = doc.lastAutoTable.finalY + 12;

      dadosComparativo.forEach((item) => {
        if (y > 250) {
          doc.addPage();
          y = 18;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(item.label, 14, y);

        const obras = Object.entries(item.obras).sort((a, b) => b[1] - a[1]);

        autoTable(doc, {
          startY: y + 5,
          head: [["OBRA", "QTD"]],
          body: obras.length ? obras : [["SEM LANÇAMENTOS", 0]],
          styles: { font: "helvetica", fontSize: 9 },
          headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255] },
        });

        y = doc.lastAutoTable.finalY + 10;
      });

      doc.save(`COMPARATIVO_LANCAMENTOS_${Date.now()}.pdf`);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      alert(
        "PARA GERAR PDF, INSTALE AS DEPENDÊNCIAS: npm install jspdf jspdf-autotable",
      );
    }
  };

  return (
    <div className="container-main">
      <button
        type="button"
        className="btn-voltar-main"
        onClick={() => navigate("/")}
        title="Voltar"
      >
        ⬅
      </button>

      <aside className="cracha-area">
        <Lanyard />
      </aside>

      <main className="main-area-geral">
        <div className="main-conteudo">
          <Boxes
            alertStatus={alertStatus}
            loading={loading}
            primeiroNome={primeiroNome}
            saudacao={saudacao}
          />

          <div className="area-nuvem-main">
            <NuvemTreinamento />
          </div>
        </div>

        <section className="dashboard-controles-mes">
          <button type="button" onClick={() => alterarMes(-1)}>
            ← MÊS ANTERIOR
          </button>

          <div>
            <span>PERÍODO SELECIONADO</span>
            <strong>{mesAtualTexto}</strong>
          </div>

          <button type="button" onClick={() => alterarMes(1)}>
            PRÓXIMO MÊS →
          </button>

          <button
            type="button"
            className="botao-mes-atual"
            onClick={voltarMesAtual}
          >
            MÊS ATUAL
          </button>
        </section>

        <section className="dashboard-main">
          <div className="dashboard-card-main total-card-main card-brilhante">
            <span>Lançamentos no mês</span>
            <strong>{loading ? "..." : dadosDashboard.totalMes}</strong>
            <small>{mesAtualTexto}</small>
          </div>

          <div className="dashboard-card-main grafico-card-main">
            <div className="titulo-grafico-main">
              <span>Treinamentos lançados por obra</span>
              <small>{mesAtualTexto}</small>
            </div>

            <div className="grafico-pizza-area">
              <div
                className="grafico-pizza"
                style={{ background: gradientePizza }}
              ></div>

              <div className="grafico-legenda">
                {dadosDashboard.porObra.length > 0 ? (
                  dadosDashboard.porObra.map((item) => (
                    <p key={item.obra} style={{ color: item.cor }}>
                      <strong>{item.obra}</strong>
                      <span>{item.quantidade}</span>
                    </p>
                  ))
                ) : (
                  <p className="grafico-vazio">
                    Nenhum lançamento neste período
                  </p>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            className="controle-lancamentos"
            onClick={() => setMostrarLancamentos((prev) => !prev)}
          >
            <span>📊</span>
            <strong>Ver lançamentos</strong>
            <small>
              {dadosDashboard.totalMes} em {mesAtualTexto}
            </small>
          </button>
        </section>

        <section className="dashboard-acoes-main">
          <button
            type="button"
            className="btn-dashboard-premium"
            onClick={() => setMostrarComparativo((prev) => !prev)}
          >
            📈 COMPARAR MESES
          </button>

          <button
            type="button"
            className="btn-dashboard-premium btn-dashboard-pdf"
            onClick={baixarPdfComparativo}
          >
            📄 BAIXAR PDF COMPARATIVO
          </button>
        </section>

        {mostrarComparativo && (
          <section className="painel-comparativo-main">
            <div className="painel-lancamentos-topo">
              <div>
                <h2>Comparativo de lançamentos</h2>
                <p>Selecione dois ou mais meses para comparar.</p>
              </div>

              <button
                type="button"
                onClick={() => setMostrarComparativo(false)}
              >
                FECHAR
              </button>
            </div>

            <div className="seletor-meses-comparativo">
              {opcoesComparativo.map((item) => (
                <label key={item.chave}>
                  <input
                    type="checkbox"
                    checked={mesesComparativo.includes(item.chave)}
                    onChange={() => alternarMesComparativo(item.chave)}
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>

            <div className="grafico-comparativo-main">
              {dadosComparativo.map((item) => (
                <div key={item.chave} className="barra-comparativo-item">
                  <span>{item.label}</span>
                  <div>
                    <strong
                      style={{
                        width: `${Math.max((item.total / maiorComparativo) * 100, 4)}%`,
                      }}
                    >
                      {item.total}
                    </strong>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {mostrarLancamentos && (
          <section className="painel-lancamentos">
            <div className="painel-lancamentos-topo">
              <div>
                <h2>Colaboradores que lançaram treinamentos</h2>
                <p>
                  {dadosDashboard.totalMes} lançamento(s) em {mesAtualTexto}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setMostrarLancamentos(false)}
              >
                FECHAR
              </button>
            </div>

            {dadosDashboard.porUsuario.length > 0 ? (
              <div className="lista-lancamentos">
                <div className="linha-lancamento cabecalho-lancamento">
                  <p>RESPONSÁVEL</p>
                  <span>OBRA</span>
                  <strong>QTD</strong>
                </div>

                {dadosDashboard.porUsuario.map((item) => (
                  <div className="linha-lancamento" key={item.nome}>
                    <p>{item.nome}</p>
                    <span>{item.obras}</span>
                    <strong>{item.quantidade}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="sem-lancamentos">
                Nenhum lançamento encontrado neste período.
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default Main;
